import { useState, useEffect } from "react";
import {
  Flex,
  View,
  Text,
  Button,
  ActionButton,
  SearchField,
  DialogTrigger,
  ProgressCircle,
} from "@adobe/react-spectrum";
import "./style.css";
import { colorPalette } from "../../constants/constants";
import { AddToSpeedDialDialog } from "./AddToSpeedDialDialog/AddToSpeedDialDialog";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { makeCall } from "../../utils/utils";
import { FilterType, SpeedDialData } from "../../types/generic.types";

import DeleteSpeedDialDialog from "./DeleteSpeedDialDialog/DeleteSpeedDialDialog";
import { ThreeDotIcon, NoSpeedDialIcon } from "../common/Icons";
import { MenuTrigger, Menu, Item } from "@adobe/react-spectrum";
import {
  getSpeedDialData,
  deleteSpeedDialContact,
} from "../../api/session-api";
import { toast } from "react-toastify";
import storeRTK from "../../store/storeRTK";
import { setSelectedTab } from "../../actions/common";
import { setCurrentSessionTab } from "../../actions/session";
import { deleteSpeedDialFromHistory, setSpeedDialHistory } from "../../actions/history";

const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const SpeedDial = () => {
  const [searchValue, setSearchValue] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [activeLetter, setActiveLetter] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [editContact, setEditContact] = useState<SpeedDialData | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<SpeedDialData | null>(
    null
  );
  const [speedDialData, setSpeedDialData] = useState<SpeedDialData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dialCallInProgress = useSelector(
    (state: RootState) => state.call.dialCallInProgress
  );

  // Function to fetch speed dial data from API
  const fetchSpeedDialData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getSpeedDialData();

      if (response.status === "success" && response.contacts) {
        // Transform API response to match SpeedDialData interface
        const transformedContacts: SpeedDialData[] = response.contacts
          .map((contact: any) => ({
            phoneNumber: contact.phoneNumber,
            firstName: contact.firstName,
            lastName: contact.lastName,
            contactType: (contact.contactType as FilterType) || "Customer", // Use API contactType or default to Customer
          }))
          .sort((a: SpeedDialData, b: SpeedDialData) => {
            // Sort alphabetically by first name, then by last name
            const firstNameComparison = a.firstName.localeCompare(b.firstName);
            if (firstNameComparison !== 0) {
              return firstNameComparison;
            }
            return a.lastName.localeCompare(b.lastName);
          });
        setSpeedDialData(transformedContacts);
        storeRTK.dispatch(setSpeedDialHistory(transformedContacts));
      } else {
        setError("Failed to fetch contacts");
        setSpeedDialData([]);
      }
    } catch (err) {
      console.error("Error fetching speed dial data:", err);
      setError("Failed to fetch contacts");
      setSpeedDialData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch speed dial data on component mount
  useEffect(() => {
    fetchSpeedDialData();
  }, []);

  // Filter contacts
  const seenNumbers = new Set<string>();

  const filteredContacts = speedDialData.filter((contact: SpeedDialData) => {
    const normalizedSearchValue = searchValue.trim().toLowerCase();
    const normalizedFullName = `${contact.firstName} ${contact.lastName}`
      .trim()
      .toLowerCase();

    const matchesSearch =
      normalizedFullName.includes(normalizedSearchValue) ||
      normalizedFullName
        .split(" ")
        .some((word) => word.includes(normalizedSearchValue)) ||
      contact.phoneNumber.includes(normalizedSearchValue);

    const matchesType =
      activeFilter === "all" || contact.contactType === activeFilter;

    const matchesLetter =
      activeLetter === "" ||
      contact.firstName.charAt(0).toUpperCase() === activeLetter;

    const isUniqueNumber = !seenNumbers.has(contact.phoneNumber);

    if (isUniqueNumber) {
      seenNumbers.add(contact.phoneNumber);
    }

    return matchesSearch && matchesType && matchesLetter && isUniqueNumber;
  });

  const handleSpeedDialDelete = async (phoneNumber: string) => {
    try {
      setLoading(true);
      const result = await deleteSpeedDialContact(phoneNumber);

      if (result) {
        toast.success("Contact deleted successfully");
        storeRTK.dispatch(deleteSpeedDialFromHistory(phoneNumber));
        setDeleteDialogOpen(false);
        // Refresh data after deletion
        await fetchSpeedDialData();
      } else {
        toast.error("Failed to delete contact. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast.error("An error occurred while deleting the contact.");
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (contact: SpeedDialData) => {
    setContactToDelete(contact);
    setDeleteDialogOpen(true);
  };

  const openEditDialog = (contact: SpeedDialData) => {
    setEditContact(contact);
    setIsOpen(true);
  };

  const handleDialogClose = () => {
    setIsOpen(false);
    setEditContact(null);
    // Refresh data after adding/editing contact
    fetchSpeedDialData();
  };

  const redirectToCallHistory = () => {
    storeRTK.dispatch(setCurrentSessionTab({ id: "call" }));
    // Use timeout to ensure the History tab selection happens after TabSwitch's automatic mapping
    setTimeout(() => {
      storeRTK.dispatch(setSelectedTab("History"));
    }, 100);
  };

  return (
    <View
      position="relative"
      height="100%"
      UNSAFE_style={{ display: "flex", flexDirection: "column" }}
    >
      {/* Header */}
      <Flex
        direction={"row"}
        justifyContent="space-between"
        alignItems="center"
        marginBottom="size-200"
        UNSAFE_className="speedDial-header"
        UNSAFE_style={{ flexShrink: 0 }}
      >
        <Text UNSAFE_className="speedDial-title">Speed Dial</Text>
        <View>
          <Button
            variant="cta"
            onPress={() => setIsOpen(true)}
            UNSAFE_className="speedDial-add-btn"
          >
            + Add new
          </Button>
        </View>
      </Flex>

      {/* Filters */}
      <Flex
        justifyContent={"space-between"}
        alignItems="center"
        marginBottom="size-200"
        UNSAFE_className="speedDial-filter-header"
        UNSAFE_style={{ flexShrink: 0 }}
      >
        <View UNSAFE_className="speedDial-filter">
          {["all", "Customer", "Internal"].map((filter) => (
            <ActionButton
              key={filter}
              marginX={8}
              aria-label={filter}
              UNSAFE_className="speedDial-filter-btn"
              onPress={() => setActiveFilter(filter as FilterType)}
              UNSAFE_style={{
                backgroundColor:
                  activeFilter === filter ? "#323232" : "transparent",
                color: activeFilter === filter ? "white" : "inherit",
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </ActionButton>
          ))}
        </View>
        <View UNSAFE_className="speedDial-search">
          <SearchField
            width={"320px"}
            value={searchValue}
            onChange={setSearchValue}
            placeholder="Search for name or mobile number"
            UNSAFE_className="speedDial-search-field"
          />
        </View>
      </Flex>

      {/* Contacts Container with proper scroll */}
      <View
        UNSAFE_className="speedDial-contacts"
        UNSAFE_style={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Flex
          direction="row"
          wrap="wrap"
          alignItems="start"
          alignContent="start"
          gap="size-200"
          UNSAFE_className="speedDial-mid-container"
          UNSAFE_style={{
            flex: 1,
            overflowY: filteredContacts.length > 0 ? "auto" : "hidden",
            overflowX: "hidden",
            height: "100%",
            paddingRight: filteredContacts.length > 0 ? "40px" : "0", // Space for alphabet filter
          }}
        >
          {loading ? (
            <Flex
              justifyContent="center"
              alignItems="center"
              width="100%"
              height="100%"
            >
              <ProgressCircle
                aria-label="Loading contacts..."
                isIndeterminate
              />
            </Flex>
          ) : error ? (
            <Flex
              justifyContent="center"
              alignItems="center"
              width="100%"
              height="100%"
            >
              <Text
                UNSAFE_style={{
                  fontSize: "16px",
                  color: "#666",
                  textAlign: "center",
                }}
              >
                {error}
              </Text>
            </Flex>
          ) : filteredContacts.length === 0 ? (
            // Check if there are no contacts at all vs filtered results
            speedDialData.length === 0 ? (
              <Flex
                justifyContent="center"
                alignItems="center"
                width="100%"
                height="100%"
                direction={"column"}
              >
                <Flex
                  justifyContent={"center"}
                  alignItems={"center"}
                  UNSAFE_className="no-speed-dial-icon"
                >
                  <NoSpeedDialIcon />
                </Flex>
                <Flex
                  justifyContent={"center"}
                  UNSAFE_className="no-speed-dial-heading"
                >
                  <Text>Speed dials is empty</Text>
                </Flex>
                <Flex
                  justifyContent={"center"}
                  UNSAFE_className="no-speed-dial-description"
                  marginTop={8}
                >
                  <Text>
                    There are no contacts added to the speed dials at this time.
                  </Text>
                </Flex>
                <Flex
                  justifyContent={"center"}
                  UNSAFE_className="no-speed-dial-description"
                >
                  <Text>
                    You can add a contact using the{" "}
                    <span style={{ fontWeight: "bold" }}>Add New</span> button or{" "}
                    <span
                      className="hyperlink-chat"
                      onClick={redirectToCallHistory}
                    >
                      history tab from call.
                    </span>{" "}
                  </Text>
                </Flex>
              </Flex>
            ) : (
              // Show "No result found" when there are contacts but search/filter returns nothing
              <Flex
                justifyContent="center"
                alignItems="center"
                width="100%"
                height="100%"
                direction={"column"}
              >
                <Flex
                  justifyContent={"center"}
                  UNSAFE_className="no-result-heading"
                >
                  <Text>No result found</Text>
                </Flex>
              </Flex>
            )
          ) : (
            filteredContacts.map((contact: SpeedDialData, index: number) => (
              <Flex
                key={index}
                alignItems="start"
                justifyContent="space-between"
                gap="size-200"
                UNSAFE_className="speedDial-container"
              >
                <Flex alignItems="start" gap="size-200">
                  <Flex
                    UNSAFE_className="speedDial-contact-initials"
                    alignItems="center"
                    justifyContent="center"
                    UNSAFE_style={{
                      backgroundColor:
                        colorPalette[index % colorPalette.length],
                    }}
                  >
                    {contact.firstName.charAt(0).toUpperCase() +
                      contact.lastName.charAt(0).toUpperCase()}
                  </Flex>
                  <Flex direction="column">
                    <Flex
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Text
                        UNSAFE_className="speedDial-contact-name"
                        UNSAFE_style={{
                          // flex: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          lineHeight: "1.2",
                          whiteSpace: "normal",
                          // maxWidth: "200px",
                        }}
                      >
                        {contact.firstName.charAt(0).toUpperCase() +
                          contact.firstName.slice(1) +
                          " " +
                          contact.lastName}
                      </Text>
                      <Text
                        UNSAFE_className="speedDial-contact-type"
                        marginStart={8}
                      >
                        {`${
                          contact.contactType === "Internal" ? "(Internal)" : ""
                        }`}
                      </Text>
                    </Flex>
                    <Text UNSAFE_className="speedDial-contact-phone">
                      {contact.phoneNumber}
                    </Text>
                  </Flex>
                </Flex>
                <Flex UNSAFE_className="speedDial-actions" alignItems="start">
                  <button
                    className="speedDial-actions-btn speedDial-actions-call-btn"
                    onClick={() =>
                      makeCall({
                        number: contact.phoneNumber,
                        dialCallInProgress,
                        speedDialContact: {
                          firstName: contact.firstName,
                          lastName: contact.lastName,
                          phoneNumber: contact.phoneNumber,
                          contactType: contact.contactType,
                        },
                      })
                    }
                  >
                    Call
                  </button>
                  <MenuTrigger>
                    <ActionButton aria-label="More actions" isQuiet>
                      <ThreeDotIcon />
                    </ActionButton>
                    <Menu
                      onAction={(key) => {
                        if (key === "edit") openEditDialog(contact);
                        if (key === "delete") openDeleteDialog(contact);
                      }}
                    >
                      <Item key="edit">Edit</Item>
                      <Item key="delete">Delete</Item>
                    </Menu>
                  </MenuTrigger>
                </Flex>
              </Flex>
            ))
          )}
        </Flex>

        {/* Alphabet Filter - only show when there are contacts */}
        {(
        <View
          UNSAFE_style={{
            position: "absolute",
            top: "0px",
            right: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            height: "100%",
            overflowY: "auto",
            paddingTop: "8px",
            paddingBottom: "8px",
          }}
        >
          {alphabets.map((letter) => (
            <div
              key={letter}
              onClick={() =>
                setActiveLetter(activeLetter === letter ? "" : letter)
              }
              className="speedDial-alphabet-filter"
              style={{
                fontWeight: activeLetter === letter ? "bold" : "normal",
                color: activeLetter === letter ? "black" : "#555",
                backgroundColor:
                  activeLetter === letter ? "#F8F8F8" : "transparent",
                border:
                  activeLetter === letter
                    ? "1px solid #ddd"
                    : "1px solid transparent",
              }}
            >
              {letter}
            </div>
          ))}
        </View>
        )}
      </View>

      {/* Add Dialog */}
      {isOpen && (
        <DialogTrigger type="modal" isOpen={isOpen} onOpenChange={setIsOpen} isKeyboardDismissDisabled>
          <div />
          <AddToSpeedDialDialog
            phoneNumber={editContact?.phoneNumber}
            firstName={editContact?.firstName}
            lastName={editContact?.lastName}
            contactType={editContact?.contactType}
            onClose={handleDialogClose}
            isEdit={
              !!(
                editContact?.phoneNumber ||
                editContact?.firstName ||
                editContact?.lastName
              )
            }
          />
        </DialogTrigger>
      )}
      {/* Delete Dialog */}
      {deleteDialogOpen && (
        <DeleteSpeedDialDialog
          isOpen={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onDelete={() => {
            if (contactToDelete) {
              handleSpeedDialDelete(contactToDelete.phoneNumber);
            }
          }}
          contactToDelete={contactToDelete}
          loading={loading}
        />
      )}
    </View>
  );
};

export default SpeedDial;
